/**
 * AdminConfig — Phase A-7
 * 3 onglets : Feature Flags / SMTP & Templates / Journal d'audit avancé.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Settings, ToggleLeft, Mail, FileText as FileTextIcon, ScrollText, Eye, EyeOff,
  Send, Download, RefreshCw, AlertTriangle, Search,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  fetchFlags, patchFlag,
  fetchSmtpConfig, patchSmtpConfig, testSmtp,
  fetchTemplates, patchTemplate,
  fetchAuditStats, fetchAuditLogs,
} from "@/lib/admin-api";
import { api } from "@/lib/api";

const ROLES = ["super_admin", "admin", "analyst", "viewer", "researcher", "user"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const fmtNum = (n) => (n ?? 0).toLocaleString("fr-FR");

export default function AdminConfig() {
  const [tab, setTab] = useState("flags");
  return (
    <div className="space-y-4" data-testid="admin-config-page">
      <div className="flex items-center gap-2">
        <Settings className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Configuration</h1>
      </div>
      <div className="flex gap-2 border-b">
        {[
          ["flags", "Feature flags", ToggleLeft],
          ["smtp", "Emails & SMTP", Mail],
          ["audit", "Journal d'audit", ScrollText],
        ].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition ${
              tab === k ? "border-b-2 border-red-700 font-semibold text-red-700 dark:text-red-400"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`config-tab-${k}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {tab === "flags" && <FlagsTab />}
      {tab === "smtp" && <SmtpTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

/* =================== FLAGS =================== */
function FlagsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try { setRows(await fetchFlags()); }
    catch { toast.error("Erreur."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggle(flag) {
    try { await patchFlag(flag.feature_key, { is_enabled: !flag.is_enabled }); load(); }
    catch { toast.error("Échec."); }
  }
  async function toggleRole(flag, role) {
    const current = flag.enabled_for_roles || [];
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    try { await patchFlag(flag.feature_key, { enabled_for_roles: next }); load(); }
    catch { toast.error("Échec."); }
  }
  async function bulkToggle(filterFn, value) {
    for (const f of rows.filter(filterFn)) {
      if (f.is_enabled !== value) await patchFlag(f.feature_key, { is_enabled: value });
    }
    load();
  }

  const registrationOff = rows.some((r) => r.feature_key === "registration" && !r.is_enabled);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => bulkToggle(() => true, true)} data-testid="flags-enable-all">
          Tout activer
        </Button>
        <Button size="sm" variant="outline"
          onClick={() => bulkToggle((f) => f.feature_key.startsWith("llm_"), false)}
          data-testid="flags-disable-llm">
          Désactiver toutes les LLM
        </Button>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {registrationOff && (
        <div className="rounded-lg border border-red-400 bg-red-50 p-3 text-xs dark:bg-red-950/20" data-testid="registration-warning">
          <AlertTriangle className="mr-1 inline h-4 w-4 text-red-600" />
          <b>Inscriptions fermées</b> — plus aucun utilisateur ne peut créer de compte.
        </div>
      )}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="flags-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Feature</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Rôles autorisés</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.feature_key} className={`border-b ${!f.is_enabled ? "opacity-60" : ""}`} data-testid={`flag-row-${f.feature_key}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{f.feature_name}</div>
                    <code className="text-[10px] text-muted-foreground">{f.feature_key}</code>
                  </td>
                  <td className="px-3 py-2 max-w-xs">{f.description}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggle(f)} data-testid={`flag-toggle-${f.feature_key}`}
                      className={`relative h-6 w-11 rounded-full transition ${f.is_enabled ? "bg-emerald-600" : "bg-muted"}`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${f.is_enabled ? "right-0.5" : "left-0.5"}`} />
                    </button>
                    <span className="ml-2 font-mono text-[10px]">{f.is_enabled ? "ON" : "OFF"}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {ROLES.map((r) => {
                        const on = (f.enabled_for_roles || []).includes(r);
                        return (
                          <button key={r} onClick={() => toggleRole(f, r)}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-mono ${on ? "bg-red-700 text-white" : "bg-muted text-muted-foreground"}`}
                            data-testid={`flag-role-${f.feature_key}-${r}`}>
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* =================== SMTP & TEMPLATES =================== */
function SmtpTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editingTpl, setEditingTpl] = useState(null);

  async function load() {
    try {
      const [c, t] = await Promise.all([fetchSmtpConfig(), fetchTemplates()]);
      setCfg(c); setForm({ sender_email: c.sender_email, sender_name: c.sender_name, reply_to: c.reply_to || "" });
      setTemplates(t);
    } catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      await patchSmtpConfig({
        sender_email: form.sender_email,
        sender_name: form.sender_name,
        reply_to: form.reply_to || null,
      });
      toast.success("Config enregistrée."); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }
  async function doTest() {
    if (!testEmail) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await testSmtp(testEmail);
      setTestResult(r.ok
        ? { ok: true, msg: `✅ Email envoyé (id ${r.data?.id || "—"})` }
        : { ok: false, msg: `❌ ${r.data?.error || "Erreur inconnue"}` });
    } catch (err) {
      setTestResult({ ok: false, msg: `❌ ${err?.response?.data?.detail || err.message}` });
    } finally { setTesting(false); }
  }

  if (!cfg) return <div className="p-6 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Configuration Resend</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Fournisseur</Label>
            <div className="font-mono text-sm"><Badge className="bg-violet-600 text-white">{cfg.provider}</Badge></div>
          </div>
          <div>
            <Label className="text-xs">Clé API</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-muted px-2 py-1 text-xs">
                {showKey ? cfg.api_key_masked : "••••••••••••••••"}
              </code>
              <Button size="sm" variant="ghost" onClick={() => setShowKey((v) => !v)} data-testid="smtp-toggle-key">
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">
              {cfg.api_key_set ? "Clé configurée via RESEND_API_KEY (backend/.env)" : "Clé manquante"}
            </div>
          </div>
          <div>
            <Label>Email expéditeur</Label>
            <Input value={form.sender_email || ""} onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))} data-testid="smtp-sender-email" />
            <div className="mt-1 text-[10px] text-muted-foreground">
              Par défaut : onboarding@resend.dev (mode test). Vérifier votre domaine sur resend.com pour sortir du mode test.
            </div>
          </div>
          <div>
            <Label>Nom expéditeur</Label>
            <Input value={form.sender_name || ""} onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))} data-testid="smtp-sender-name" />
          </div>
          <div>
            <Label>Reply-to (optionnel)</Label>
            <Input value={form.reply_to || ""} onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))} data-testid="smtp-reply-to" />
          </div>
          <Button onClick={save} data-testid="smtp-save">Enregistrer la config</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Test d'envoi</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Destinataire de test</Label>
            <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="vous@exemple.com" data-testid="smtp-test-email" />
          </div>
          <Button onClick={doTest} disabled={testing || !testEmail} data-testid="smtp-test-btn">
            <Send className="mr-1 h-4 w-4" /> {testing ? "Envoi…" : "Envoyer email de test"}
          </Button>
          {testResult && (
            <div className={`rounded border p-3 text-xs ${testResult.ok ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20" : "border-red-400 bg-red-50 dark:bg-red-950/20"}`} data-testid="smtp-test-result">
              {testResult.msg}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Templates d'emails ({templates.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="templates-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Clé</th>
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2">Objet</th>
                <th className="px-3 py-2">Variables</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.key} className="border-b">
                  <td className="px-3 py-2 font-mono">{t.key}</td>
                  <td className="px-3 py-2">{t.name}</td>
                  <td className="px-3 py-2 max-w-xs truncate">{t.subject}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(t.variables || []).map((v) => (
                        <code key={v} className="rounded bg-muted px-1 text-[10px]">{`{{${v}}}`}</code>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditingTpl(t)} data-testid={`tpl-edit-${t.key}`}>
                      <FileTextIcon className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editingTpl && (
        <TemplateEditor tpl={editingTpl} onClose={() => setEditingTpl(null)}
          onSaved={() => { setEditingTpl(null); load(); }} />
      )}
    </div>
  );
}

function TemplateEditor({ tpl, onClose, onSaved }) {
  const [form, setForm] = useState({ subject: tpl.subject, body_html: tpl.body_html });
  const preview = useMemo(() => {
    let html = form.body_html || "";
    (tpl.variables || []).forEach((v) => {
      html = html.replaceAll(`{{${v}}}`, `<b style="color:#7f1d1d">[${v}]</b>`);
    });
    return html;
  }, [form.body_html, tpl.variables]);

  async function save() {
    try { await patchTemplate(tpl.key, form); toast.success("Template enregistré."); onSaved(); }
    catch { toast.error("Échec."); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="tpl-editor">
        <DialogHeader>
          <DialogTitle>Éditer « {tpl.name} »</DialogTitle>
          <DialogDescription>Clé : <code>{tpl.key}</code></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Objet</Label>
            <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="tpl-subject" />
          </div>
          <div>
            <Label>Corps HTML</Label>
            <Textarea rows={8} value={form.body_html}
              onChange={(e) => setForm((f) => ({ ...f, body_html: e.target.value }))} data-testid="tpl-body" />
          </div>
          <div className="rounded border bg-muted/30 p-3">
            <div className="mb-1 text-[10px] uppercase text-muted-foreground">Aperçu (variables en rouge)</div>
            <div dangerouslySetInnerHTML={{ __html: preview }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} data-testid="tpl-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =================== AUDIT =================== */
function AuditTab() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: "", user_email: "", status: "", critical_only: false });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        fetchAuditStats().catch(() => null),
        fetchAuditLogs({
          page, limit: 50,
          action: filters.action || undefined,
          user_email: filters.user_email || undefined,
          status: filters.status || undefined,
          critical_only: filters.critical_only || undefined,
        }),
      ]);
      setStats(s);
      setRows(r.data || []);
      setTotal(r.total || 0);
    } catch { toast.error("Erreur."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filters]);

  async function exportCsv() {
    try {
      const params = new URLSearchParams();
      if (filters.action) params.set("action", filters.action);
      if (filters.user_email) params.set("user_email", filters.user_email);
      if (filters.status) params.set("status", filters.status);
      const res = await api.get(`/admin/audit/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "audit_log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export échoué."); }
  }

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const statusColor = (s) => {
    if (!s) return "bg-slate-500 text-white";
    if (s >= 500) return "bg-red-700 text-white";
    if (s >= 400) return "bg-amber-600 text-white";
    if (s >= 200) return "bg-emerald-600 text-white";
    return "bg-slate-500 text-white";
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase">Par statut (30 j)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {stats?.by_status?.map((s) => (
                <div key={s.status} className="flex items-center gap-2 text-xs">
                  <Badge className={statusColor(s.status)}>{s.status || "—"}</Badge>
                  <div className="flex-1 overflow-hidden rounded bg-muted">
                    <div className={`h-3 ${statusColor(s.status).split(" ")[0]}`}
                      style={{ width: `${(s.count / (stats?.by_status?.[0]?.count || 1)) * 100}%` }} />
                  </div>
                  <span className="w-14 text-right font-mono">{fmtNum(s.count)}</span>
                </div>
              )) || <div className="py-4 text-center text-xs text-muted-foreground">Pas de données.</div>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase">Top 5 actions</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-1 text-xs">
              {stats?.top_actions?.map((a, i) => (
                <li key={a.action} className="flex justify-between"><code>{i + 1}. {a.action}</code> <span className="font-mono">{fmtNum(a.count)}</span></li>
              )) || <li className="text-muted-foreground">—</li>}
            </ol>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs uppercase">Top 5 utilisateurs</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-1 text-xs">
              {stats?.top_users?.map((u, i) => (
                <li key={u.user_email} className="flex justify-between"><span>{i + 1}. {u.user_email}</span> <span className="font-mono">{fmtNum(u.count)}</span></li>
              )) || <li className="text-muted-foreground">—</li>}
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Filters + export */}
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">Action</Label>
          <Input value={filters.action} placeholder="admin.user.*" className="h-9 w-48"
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, action: e.target.value })); }} data-testid="audit-f-action" /></div>
        <div><Label className="text-xs">Email</Label>
          <Input value={filters.user_email} placeholder="vitae-publica.org" className="h-9 w-56"
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, user_email: e.target.value })); }} data-testid="audit-f-email" /></div>
        <div><Label className="text-xs">Statut</Label>
          <Input value={filters.status} placeholder="200 / 403 / 500" className="h-9 w-32"
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }} data-testid="audit-f-status" /></div>
        <label className="mb-1 ml-2 flex items-center gap-1 text-xs">
          <input type="checkbox" checked={filters.critical_only}
            onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, critical_only: e.target.checked })); }}
            data-testid="audit-f-critical" />
          Critiques seulement
        </label>
        <Button variant="outline" onClick={exportCsv} data-testid="audit-export-csv">
          <Download className="mr-1 h-4 w-4" /> Export CSV
        </Button>
        <Button variant="ghost" size="icon" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="audit-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">IP</th>
                <th className="px-3 py-2">Ressource</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b">
                  <td className="px-3 py-2 font-mono text-[10px]">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 font-mono">{r.action}</td>
                  <td className="px-3 py-2">{r.user_email || r.user_id?.slice(0, 8) || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{r.ip || "—"}</td>
                  <td className="px-3 py-2 max-w-xs truncate font-mono text-[10px]">{r.resource || "—"}</td>
                  <td className="px-3 py-2"><Badge className={statusColor(r.status)}>{r.status || "—"}</Badge></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucun log.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {total > 50 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Préc.</Button>
          <span className="text-xs">Page {page} / {totalPages} — {fmtNum(total)} entrées</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Suiv.</Button>
        </div>
      )}
    </div>
  );
}
